import React, { useState } from 'react';
import { CreditCard, Clock, Percent, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { formatMoney, formatMoneyDetailed } from '../../utils/format';
import AmortizationTable from './AmortizationTable';
import useFinanceData from './useFinanceData';

/**
 * Finance — onglet Crédits. Liste des emprunts actifs avec progress bar
 * et tableau d'amortissement repliable. Extracted from Finance.jsx on 28/05/2026.
 * (28/05/2026 v2 — Le bloc Amortissements de la flotte a été déplacé dans
 *  son propre onglet dédié `AmortissementsTab`.)
 */
export default function CreditsTab() {
  const { t } = useTranslation();
  const { credits, totalDebt, monthlyCredit } = useFinanceData();
  const [showAmortization, setShowAmortization] = useState(null);

  return (
    <div className="space-y-4">
      {/* Debt summary */}
      <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <p className="text-xs text-slate-500">{t('total_debt')}</p>
            <p className="text-lg font-bold text-danger">{formatMoney(totalDebt)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">{t('monthly_payments')}</p>
            <p className="text-lg font-bold text-warn">{formatMoney(monthlyCredit)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">{t('num_credits')}</p>
            <p className="text-lg font-bold text-foreground">{credits.length}</p>
          </div>
        </div>
      </div>

      {/* Active Credits */}
      <div className="bg-ink-800 rounded-lg border border-ink-600 p-4" data-testid="credits-section">
        <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
          <CreditCard className="w-4 h-4" /> {t('active_credits')}
        </h3>
        {credits.length === 0 ? (
          <p className="text-sm text-slate-500">{t('no_credit')}</p>
        ) : (
          <div className="space-y-3">
            {credits.map((c, idx) => {
              const progress = ((c.durationMonths - c.remainingMonths) / c.durationMonths) * 100;
              return (
                <div key={c.id} className="border border-ink-600 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-slate-500">{t('credit_number')} #{idx + 1}</span>
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {c.remainingMonths} {t('months_left')}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                    <div>
                      <span className="text-slate-500">{t('capital')}</span>
                      <p className="font-bold text-slate-200">{formatMoney(Number.isFinite(c.principal) ? c.principal : 0)}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">{t('monthly_payment')}</span>
                      <p className="font-bold text-danger">{formatMoneyDetailed(Number.isFinite(c.monthlyPayment) ? c.monthlyPayment : 0)}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">{t('remaining_due')}</span>
                      <p className="font-bold text-slate-300">{formatMoney(Math.max(0, Number.isFinite(c.remainingPrincipal) ? c.remainingPrincipal : 0))}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">{t('rate')}</span>
                      <p className="font-bold text-slate-300 flex items-center gap-0.5"><Percent className="w-3 h-3" />{((Number.isFinite(c.annualRate) ? c.annualRate : 0) * 100).toFixed(1)}</p>
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 rounded-full bg-ink-700/40 overflow-hidden">
                      <div className="h-full rounded-full bg-accent transition-all duration-700" style={{ width: `${progress}%` }} />
                    </div>
                    <span className="text-[10px] text-slate-500 w-10 text-right">{Math.round(progress)}%</span>
                  </div>

                  {/* Amortization toggle */}
                  <button
                    onClick={() => setShowAmortization(showAmortization === c.id ? null : c.id)}
                    className="mt-2 text-[10px] text-foreground hover:underline flex items-center gap-0.5"
                    data-testid={`toggle-amort-${c.id}`}
                  >
                    {showAmortization === c.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    {t('amortization_table')}
                  </button>
                  {showAmortization === c.id && (
                    <AmortizationTable principal={c.principal} annualRate={c.annualRate} months={c.durationMonths} />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
